import * as React from "react"

import { Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import {
  FinanceOverviewChartId,
  } from "@/features/finance/model/finance-normalized-overview"
import { buildHorizontalBarRows, findChartById } from "@/features/finance/lib/finance-overview-integration"
import type { FinanceOverviewQuery } from "@/features/finance/model/finance-types"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { ProgressBar } from "@/features/finance/overview/components/progress-bar"
import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"
import { formatMoneySafe, formatPercent } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import {
  FinanceDisclosureLevel,
  type FinanceSurfaceContext,
} from "@/features/finance/hooks/finance-context-rules"

const MAX_ROWS = 5

interface AccountRow {
  label: string
  value: number
  share: number
}

interface IncomeByAccountData {
  currency: string
  rows: AccountRow[]
}

/**
 * Income by Account — a ratio/share breakdown of where income comes from over the selected period.
 * PERIOD-SENSITIVE (reuses the overview query). Rendered as ranked share rows (label + amount + % of
 * total, with a progress bar), not a chart, so long account names stay readable in a one-column card.
 */
export function IncomeByAccountWidget({ context }: { context?: FinanceSurfaceContext } = {}) {
  /*
   * Under a withheld scope the server already sends this row's value AS a share, so printing it
   * beside the share bar shows the same percentage twice. The widget knows which it is holding from
   * the board's context — not by sniffing the payload, which is what it used to have to do.
   */
  const showsAmount = context?.disclosure !== FinanceDisclosureLevel.Shares;
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const period = useFinanceOverviewPeriod()
  const go = useOverviewNav()
  const { useFinanceOverviewQuery } = useFinanceFeatureQuery()

  const query = useFinanceOverviewQuery(
    scope.workspaceId && period.from && period.to
      ? ({
          workspaceId: scope.workspaceId,
          context: scope.overviewContext,
          branchId: scope.branchId ?? undefined,
          dateFrom: period.from,
          dateTo: period.to,
          // NOTE: omit `profile` — the dashboard profile trims account breakdowns; we need them here.
        } satisfies FinanceOverviewQuery)
      : null,
  )

  const data = React.useMemo<IncomeByAccountData | undefined>(() => {
    if (!query.data) return undefined
    const response = query.data
    const chart = findChartById(response, FinanceOverviewChartId.IncomeByCategory)
    // Rank income accounts by amount and show the top few. The share is recomputed over the SHOWN rows so the
    // displayed slices are internally consistent and sum to 100% — the backend's whole-set basis-point share was
    // allocated over EVERY account (before this top-N slice), so the shown rows never summed to 100% (IBA-117 follow-up).
    const top = (chart ? buildHorizontalBarRows(chart) : [])
      .filter((bar) => bar.net > 0)
      .sort((a, b) => b.net - a.net)
      .slice(0, MAX_ROWS)
    const shownTotal = top.reduce((sum, bar) => sum + bar.net, 0)
    const rows = top.map((bar) => ({
      // Already in the reader's language: the API localizes seeded account names (ADR-0030).
      label: bar.label,
      value: bar.net,
      share: shownTotal > 0 ? (bar.net / shownTotal) * 100 : 0,
    }))
    return { currency: response.meta?.currency ?? scope.currency, rows }
  }, [query.data, scope.currency])

  useWidgetMenu({
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.transactions) ? () => go(OverviewRoutes.transactions) : undefined,
  })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={data}
      isEmpty={(d) => d.rows.length === 0}
      skeleton={<AccountSkeleton />}
      errorTitle={t("widgets.incomeByAccount.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.incomeByAccount.empty.title")}
    >
      {(d) => (
        <ul className="space-y-2.5">
          {d.rows.map((row) => (
            <li key={row.label} className="space-y-1">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 flex-1 truncate">{row.label}</span>
                {showsAmount ? (
                  <span className="shrink-0 font-medium tabular-nums">
                    {formatMoneySafe(row.value, d.currency)}
                  </span>
                ) : null}
                <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {formatPercent(row.share)}
                </span>
              </div>
              <ProgressBar value={row.share} tone="success" aria-label={row.label} />
            </li>
          ))}
        </ul>
      )}
    </WidgetBoundary>
  )
}

function AccountSkeleton() {
  return (
    <div className="space-y-2.5">
      {[70, 55, 62, 48, 40].map((w, index) => (
        <div key={index} className="space-y-1">
          <div className="flex items-center justify-between">
            <Skeleton className="h-3.5" style={{ width: `${w}%` }} />
            <Skeleton className="h-3.5 w-14" />
          </div>
          <Skeleton className="h-1.5 w-full" />
        </div>
      ))}
    </div>
  )
}
